import React from 'react';
import { 
  Container, 
  Title, 
  Text, 
  Accordion, 
  Box,
  rem 
} from '@mantine/core';
import { createStyles } from '@mantine/styles';
import { Header } from '../landing/Header';
import { AuthenticatedHeader } from '../landing/AuthenticatedHeader';
import { Footer } from '../landing/Footer';
import { useAuth } from '../UseAuthHook';
import { useNavigate } from 'react-router-dom';

const useStyles = createStyles((theme: any) => ({
  wrapper: {
    paddingTop: rem(120), // Increased padding to prevent header overlap
    paddingBottom: rem(50),
    display: 'flex',
    flexDirection: 'column',
  },
  
  title: {
    marginTop: rem(30), // Added top margin for more spacing
    marginBottom: rem(30),
    textAlign: 'center',
    fontWeight: 800,
    fontSize: rem(38), // Increased font size
    letterSpacing: -1,
    color: theme.colorScheme === 'dark' ? theme.white : theme.black,
    
    [theme.fn.smallerThan('sm')]: {
      fontSize: rem(24),
      textAlign: 'left',
    },
  },
  
  description: {
    textAlign: 'center',
    marginBottom: rem(50), // Increased bottom margin
    fontSize: rem(18), // Increased font size for better readability
    
    [theme.fn.smallerThan('sm')]: {
      textAlign: 'left',
    },
  },
  
  item: {
    borderRadius: theme.radius.md,
    marginBottom: theme.spacing.lg,
    border: `${rem(1)} solid ${
      theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]
    }`,
  },
  
  content: {
    flex: '1 1 auto',
  }
}));

const faqData = [
  {
    question: "How does the AI face recognition work?",
    answer: "Our AI uses advanced facial recognition technology to identify and group photos of the same person. You can tag people once, and the system will learn to recognize them in other photos. The more you use it, the smarter it gets at identifying your family and friends."
  },
  {
    question: "Do I need to scan my own photos?",
    answer: "You can upload photos in any way that works for you! You can use a scanning service, do it yourself with a scanner, or use our mobile app to quickly capture physical photos. Check out our Photo Scanning Guide for best practices and recommendations."
  },
  {
    question: "Is my data secure and private?",
    answer: "Absolutely. We take privacy very seriously. Your photos are stored securely, and we never share your data with third parties. You have complete control over who can access your photos, and you can delete your data at any time."
  },
  {
    question: "Can I organize photos by events or dates?",
    answer: "Yes! Our AI helps predict dates for undated photos based on visual cues, similar photos, and other metadata. You can also create custom events, add location data, and organize photos into albums for easy browsing."
  },
  {
    question: "Can I share my photos with family members?",
    answer: "Yes! You can easily share individual photos, albums, or your entire collection with family members. You can control exactly what they can view and whether they can add their own tags and information."
  },
  {
    question: "How does the date prediction work?",
    answer: "Our AI analyzes visual elements in your photos (clothing styles, cars, technology), cross-references with other dated photos, and uses any existing metadata to make intelligent predictions about when undated photos were taken."
  },
  {
    question: "Can I export my organized photos?",
    answer: "Yes, you can export your photos with all the metadata you've added. This ensures that your organization work isn't locked into our platform, and you can use your organized collection with other software or services."
  }
];

export function FAQPage() {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  // Function to handle scrolling to sections on the landing page
  const handleSectionScroll = (sectionId: string) => {
    navigate('/');
    // We need to wait for navigation to complete before scrolling
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };
  
  // Force header shadow for better visual separation
  const forceHeaderShadow = () => {
    const headerElements = document.querySelectorAll('header');
    headerElements.forEach(header => {
      header.style.boxShadow = '0px 2px 10px rgba(0, 0, 0, 0.1)';
    });
  };

  // Apply shadow effect when component mounts
  React.useEffect(() => {
    forceHeaderShadow();
    const timer = setTimeout(forceHeaderShadow, 100);
    return () => clearTimeout(timer);
  }, []);

  const items = faqData.map((item) => (
    <Accordion.Item key={item.question} className={classes.item} value={item.question}>
      <Accordion.Control>{item.question}</Accordion.Control>
      <Accordion.Panel>{item.answer}</Accordion.Panel>
    </Accordion.Item>
  ));

  return (
    <Container fluid p={0} style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh' 
    }}>
      {isAuthenticated ? (
        <AuthenticatedHeader 
          onSortClick={() => navigate('/gallery')} 
          scrollToSection={(sectionId) => handleSectionScroll(sectionId)}
        />
      ) : (
        <Header 
          onLoginClick={() => navigate('/login')} 
          onRegisterClick={() => navigate('/signup')}
          scrollToSection={(sectionId) => handleSectionScroll(sectionId)} 
        />
      )}
      
      <Box className={classes.content} style={{ flex: '1 0 auto' }}>
        <Container size="lg" className={classes.wrapper}>
          <Title mt="xl" className={classes.title}>Frequently Asked Questions</Title>
          <Text mt="sm" c="dimmed" className={classes.description}>
            Find answers to common questions about SortMyShoebox.
          </Text>

          <Accordion mt="lg">{items}</Accordion>
        </Container>
      </Box>
      
      <Footer />
    </Container>
  );
}
